import { useState, useEffect, useCallback, useRef } from 'react';
import { LeaderboardEntry } from '@/domain/types';
import { tapApi } from '@/services/tapApi';

export interface LeaderboardState {
  entries: LeaderboardEntry[];
  aroundMe: LeaderboardEntry[];
  userRank: number | null;
  isLoading: boolean;
  error: string | null;
}

export function useLeaderboard(): [LeaderboardState, () => Promise<void>] {
  const [state, setState] = useState<LeaderboardState>({
    entries: [],
    aroundMe: [],
    userRank: null,
    isLoading: true,
    error: null,
  });

  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const data = await tapApi.getLeaderboard();
      setState({
        entries: data?.entries ?? [],
        aroundMe: data?.aroundMe ?? [],
        userRank: data?.userRank ?? null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load leaderboard',
      }));
    }
  }, []);

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    refresh();
  }, [refresh]);

  return [state, refresh];
}
